import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { superadminApi } from "../services/api";
import EmptyState from "../components/EmptyState";
import Modal from "../components/Modal";
import { colorFor, formatDate, initials, timeAgo } from "../utils/format";

const ACCESS_LABELS = {
  none: { label: "No access", tag: "" },
  pending: { label: "Requested", tag: "tag-warning" },
  approved: { label: "Has access", tag: "tag-success" },
  rejected: { label: "Rejected", tag: "tag-danger" },
  revoked: { label: "Revoked", tag: "tag-danger" },
};

const FILTERS = [
  { id: "all", label: "All", params: {} },
  { id: "approved", label: "Has access", params: { access: "approved" } },
  { id: "pending", label: "Requested", params: { access: "pending" } },
  { id: "none", label: "No access", params: { access: "none" } },
  { id: "revoked", label: "Revoked", params: { access: "revoked" } },
  { id: "admin", label: "Admins", params: { role: "admin" } },
  { id: "superadmin", label: "Superadmins", params: { role: "superadmin" } },
  { id: "suspended", label: "Suspended", params: { suspended: "true" } },
  { id: "deleted", label: "Soft-deleted", params: { deleted: "true" } },
  { id: "unverified", label: "Unconfirmed email", params: { verified: "false" } },
];

const DEFAULT_CREDITS = 100;
const money = (n) =>
  n == null || Number.isNaN(n)
    ? "—"
    : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const creditsLeft = (user) => Math.max(0, (user.access?.credits ?? 0) - (user.access?.creditsUsed ?? 0));
const hasCredits = (user) => user.role === "user" && user.access?.status === "approved";

function RoleTag({ user }) {
  if (user.role === "superadmin") return <span className="tag tag-info">Superadmin</span>;
  if (user.role === "admin") return <span className="tag tag-info">Admin</span>;
  if (user.deletedAt) return <span className="tag tag-danger">Soft-deleted</span>;
  if (user.access?.wantsMore) return <span className="tag tag-warning">Wants more credits</span>;
  if (hasCredits(user) && creditsLeft(user) === 0) return <span className="tag tag-warning">Out of credits</span>;
  const { label, tag } = ACCESS_LABELS[user.access?.status] || ACCESS_LABELS.none;
  return <span className={`tag ${tag}`}>{label}</span>;
}

function UserName({ user, me }) {
  return (
    <div className="d-flex align-items-center gap-2 min-w-0">
      <span className="avatar avatar-sm" style={{ background: colorFor(user.name) }}>
        {initials(user.name)}
      </span>
      <div className="min-w-0">
        <div className="fw-medium text-truncate">
          {user.name}
          {user.id === me && <span className="text-muted fw-normal"> (you)</span>}
          {user.disabled && <span className="tag tag-danger ms-2">Suspended</span>}
          {user.deletedAt && <span className="tag tag-danger ms-2">Deleted</span>}
          {!user.emailVerified && (
            <span className="tag tag-warning ms-2" title="Hasn't confirmed email">
              Unconfirmed
            </span>
          )}
        </div>
        <div className="small text-muted text-truncate">{user.email}</div>
      </div>
    </div>
  );
}

function MonthBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="sa-month-bars">
      {rows.map((r) => (
        <div key={r.month} className="sa-month-bar" title={`${r.month}: ${r.count}`}>
          <div className="sa-month-fill" style={{ height: `${Math.max(4, (r.count / max) * 100)}%` }} />
          <span className="sa-month-label">{r.month.slice(5)}</span>
          <span className="sa-month-count">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

function LogisticsTab({ data }) {
  if (!data) return null;
  const { users, usage, openai, jobs } = data;
  const cards = [
    { label: "Active users", value: users.total, hint: `${users.newThisWeek} new this week`, icon: "bi-people", color: "#708090" },
    { label: "New (30 days)", value: users.newThisMonth, hint: `${users.pending} access requests`, icon: "bi-person-plus", color: "#36454f" },
    { label: "With AI access", value: users.approved, hint: `${users.suspended} suspended · ${users.softDeleted} soft-deleted`, icon: "bi-unlock", color: "#10b981" },
    { label: "Jobs tracked", value: jobs.total, hint: "Across all users", icon: "bi-briefcase", color: "#6366f1" },
    { label: "AI calls today", value: usage.aiToday, hint: `${usage.ai30} in 30 days`, icon: "bi-stars", color: "#ec4899" },
    {
      label: "Est. OpenAI (30d)",
      value: money(openai.estimatedSpend30dUsd),
      hint: openai.budgetUsd
        ? `${money(openai.budgetRemainingUsd)} of ${money(openai.budgetUsd)} left (est.)`
        : `~${money(openai.estimatedCostPerCallUsd)} / call · set OPENAI_BUDGET_USD to track remaining`,
      icon: "bi-currency-dollar",
      color: "#f59e0b",
    },
  ];

  return (
    <>
      <div className="stat-grid mb-4">
        {cards.map((c) => (
          <div key={c.label} className="stat-card" style={{ "--stage-color": c.color }}>
            <div className="d-flex justify-content-between align-items-center">
              <span className="small text-muted">{c.label}</span>
              <i className={`bi ${c.icon} stat-icon`} />
            </div>
            <div className="stat-value" style={{ fontSize: typeof c.value === "string" ? "1.35rem" : undefined }}>
              {c.value}
            </div>
            <div className="stat-hint">{c.hint}</div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-body">
              <h2 className="h6 mb-1">Signups by month</h2>
              <p className="small text-muted mb-3">Active accounts created (last {users.signupsByMonth.length} months).</p>
              <MonthBars rows={users.signupsByMonth} />
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-body">
              <h2 className="h6 mb-1">OpenAI budget check</h2>
              <p className="small text-muted mb-3">{openai.note}</p>
              <dl className="row small mb-0">
                <dt className="col-6 text-muted">Key configured</dt>
                <dd className="col-6">{openai.configured ? "Yes" : "No"}</dd>
                <dt className="col-6 text-muted">Model</dt>
                <dd className="col-6">{openai.model}</dd>
                <dt className="col-6 text-muted">Est. spend (30d)</dt>
                <dd className="col-6">{money(openai.estimatedSpend30dUsd)}</dd>
                <dt className="col-6 text-muted">Budget</dt>
                <dd className="col-6">{openai.budgetUsd ? money(openai.budgetUsd) : "Not set"}</dd>
                <dt className="col-6 text-muted">Staff</dt>
                <dd className="col-6">
                  {users.admins} admin · {users.superadmins} superadmin
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ServicesTab({ data }) {
  if (!data) return null;
  return (
    <div className="card">
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Service</th>
              <th>Status</th>
              <th className="d-none d-md-table-cell">Purpose</th>
              <th className="d-none d-lg-table-cell">Where to renew</th>
            </tr>
          </thead>
          <tbody>
            {data.services.map((s) => (
              <tr key={s.id}>
                <td>
                  <div className="fw-medium">{s.name}</div>
                  <div className="small text-muted">{s.detail}</div>
                </td>
                <td>
                  <span className={`tag ${s.configured ? "tag-success" : "tag-warning"}`}>
                    {s.configured ? "Configured" : "Missing"}
                  </span>
                </td>
                <td className="small text-muted d-none d-md-table-cell">{s.purpose}</td>
                <td className="small d-none d-lg-table-cell">{s.renewAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-body border-top small text-muted">
        Optional env for estimates: <code>OPENAI_BUDGET_USD</code>, <code>OPENAI_EST_COST_PER_CALL</code> (currently{" "}
        {money(data.openaiBudget?.estCostPerCallUsd)}). Exact prepaid balances live in each vendor&apos;s dashboard.
      </div>
    </div>
  );
}

function CreditsInput({ id, value, onChange }) {
  return (
    <div className="input-group input-group-sm flex-shrink-0" style={{ width: 150 }}>
      <input
        id={id}
        type="number"
        className="form-control"
        min={1}
        max={10000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="AI credits to give"
      />
      <span className="input-group-text">credits</span>
    </div>
  );
}

function EditUserModal({ user, me, onClose, onSaved, onDeleted }) {
  const { toast } = useApp();
  const self = user.id === me;
  const softDeleted = Boolean(user.deletedAt);
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    role: user.role === "superadmin" ? "superadmin" : user.role,
    disabled: Boolean(user.disabled),
    emailVerified: Boolean(user.emailVerified),
  });
  const [note, setNote] = useState(user.access?.note || "");
  const [credits, setCredits] = useState(user.access?.credits || DEFAULT_CREDITS);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [busy, setBusy] = useState("");

  const save = async () => {
    setBusy("save");
    try {
      const changes = {
        name: form.name,
        email: form.email,
        disabled: form.disabled,
        emailVerified: form.emailVerified,
      };
      changes.role = form.role;
      const { user: updated } = await superadminApi.updateUser(user.id, changes);
      onSaved(updated, "Saved");
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setBusy("");
    }
  };

  const setAccess = async (action, message) => {
    setBusy(action);
    try {
      const { user: updated } = await superadminApi.setAccess(
        user.id,
        action,
        note,
        action === "approve" ? Number(credits) : undefined
      );
      onSaved(updated, message);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setBusy("");
    }
  };

  const softDelete = async () => {
    setBusy("soft");
    try {
      const { user: updated } = await superadminApi.softDeleteUser(user.id);
      onSaved(updated, `Soft-deleted ${user.name}`);
      onClose();
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setBusy("");
    }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      const { user: updated } = await superadminApi.restoreUser(user.id);
      onSaved(updated, `Restored ${user.name}`);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setBusy("");
    }
  };

  const hardDelete = async () => {
    setBusy("delete");
    try {
      await superadminApi.hardDeleteUser(user.id);
      onDeleted(user);
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setBusy("");
    }
  };

  const accessButtons = [
    { action: "approve", label: "Approve / top up", variant: "btn-primary", message: "Access approved" },
    { action: "reject", label: "Reject", variant: "btn-outline-secondary", message: "Request rejected" },
    { action: "revoke", label: "Revoke", variant: "btn-outline-danger", message: "Access revoked", title: "Remove AI access" },
    { action: "reset", label: "Reset", variant: "btn-outline-secondary", message: "Access reset", title: "Clear request state" },
  ];

  return (
    <Modal show title={`Manage ${user.name}`} onClose={onClose} size="modal-lg">
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label" htmlFor="sa-name">
            Name
          </label>
          <input
            id="sa-name"
            className="form-control form-control-sm"
            value={form.name}
            disabled={softDeleted}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="sa-email">
            Email
          </label>
          <input
            id="sa-email"
            type="email"
            className="form-control form-control-sm"
            value={form.email}
            disabled={softDeleted}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label" htmlFor="sa-role">
            Role
          </label>
          <select
            id="sa-role"
            className="form-select form-select-sm"
            value={form.role}
            disabled={self || softDeleted}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            <option value="user">User</option>
            <option value="admin">Admin (uses the app + /admin)</option>
            <option value="superadmin">Superadmin (ops only — no job hunting)</option>
          </select>
          {self && <div className="form-text">You can&apos;t change your own role.</div>}
        </div>
        <div className="col-md-6 d-flex align-items-end gap-3">
          <div className="form-check form-switch mb-2">
            <input
              id="sa-disabled"
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={form.disabled}
              disabled={self || softDeleted}
              onChange={(e) => setForm({ ...form, disabled: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="sa-disabled">
              Suspended
            </label>
          </div>
          <div className="form-check form-switch mb-2">
            <input
              id="sa-verified"
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={form.emailVerified}
              disabled={self || softDeleted}
              onChange={(e) => setForm({ ...form, emailVerified: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="sa-verified">
              Email confirmed
            </label>
          </div>
        </div>
      </div>

      {!softDeleted && (
        <div className="mt-3">
          <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={save}>
            {busy === "save" && <span className="spinner-border spinner-border-sm me-1" />}
            Save changes
          </button>
        </div>
      )}

      {user.role === "user" && !softDeleted && (
        <>
          <hr className="my-4" />
          <h6 className="mb-2 d-flex align-items-center gap-2">
            AI access <RoleTag user={user} />
          </h6>
          <div className="small text-muted mb-2">
            {user.access?.requestedAt && <>Requested {formatDate(user.access.requestedAt)}. </>}
            {hasCredits(user) && (
              <>
                Used {user.access.creditsUsed} of {user.access.credits} credits.{" "}
              </>
            )}
            {user.usage && <>{user.usage.ai30} AI calls in 30 days.</>}
          </div>
          {user.access?.reason && <blockquote className="request-reason mb-3">{user.access.reason}</blockquote>}
          <input
            className="form-control form-control-sm mb-2"
            placeholder="Optional note they'll see"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="d-flex flex-wrap gap-2">
            <CreditsInput id="sa-credits" value={credits} onChange={setCredits} />
            {accessButtons.map((b) => (
              <button
                key={b.action}
                className={`btn btn-sm ${b.variant}`}
                title={b.title}
                disabled={!!busy || (b.action === "approve" && !(Number(credits) >= 1))}
                onClick={() => setAccess(b.action, b.message)}
              >
                {busy === b.action && <span className="spinner-border spinner-border-sm me-1" />}
                {b.label}
              </button>
            ))}
          </div>
        </>
      )}

      {!self && (
        <>
          <hr className="my-4" />
          <h6 className="mb-2">Delete</h6>
          {softDeleted ? (
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <p className="small text-muted mb-0 flex-grow-1">
                Soft-deleted {user.deletedAt ? timeAgo(user.deletedAt) : ""}. Restore to let them sign in again, or hard-delete permanently.
              </p>
              <button className="btn btn-outline-primary btn-sm" disabled={!!busy} onClick={restore}>
                {busy === "restore" && <span className="spinner-border spinner-border-sm me-1" />}
                Restore
              </button>
            </div>
          ) : (
            <button className="btn btn-outline-warning btn-sm mb-3" disabled={!!busy || user.role === "superadmin"} onClick={softDelete}>
              {busy === "soft" && <span className="spinner-border spinner-border-sm me-1" />}
              Soft delete (can restore)
            </button>
          )}
          <p className="small text-muted mb-2">
            Permanently delete {user.name}&apos;s account, jobs, and documents. Type their email to confirm.
          </p>
          <div className="d-flex gap-2">
            <input
              className="form-control form-control-sm"
              placeholder={user.email}
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
            />
            <button
              className="btn btn-danger btn-sm flex-shrink-0"
              disabled={!!busy || confirmDelete.trim().toLowerCase() !== user.email}
              onClick={hardDelete}
            >
              {busy === "delete" && <span className="spinner-border spinner-border-sm me-1" />}
              Hard delete
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function UsersTab({ me, version, onEdit }) {
  const { toast } = useApp();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(q.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { ...(FILTERS.find((f) => f.id === filter)?.params || {}), page };
    if (query) params.q = query;
    superadminApi
      .users(params)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) toast(err.message, "danger");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter, query, page, version, toast]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 260 }}
          placeholder="Search name or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="tab-scroll flex-grow-1 mb-0">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`tab-btn ${filter === f.id ? "active" : ""}`}
              onClick={() => {
                setFilter(f.id);
                setPage(1);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : !data?.users?.length ? (
        <div className="card">
          <EmptyState icon="bi-people" title="No users match" text="Try another filter or search." />
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access</th>
                  <th className="d-none d-md-table-cell">Jobs</th>
                  <th className="d-none d-lg-table-cell">AI (30d)</th>
                  <th className="d-none d-lg-table-cell">Joined</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <UserName user={u} me={me} />
                    </td>
                    <td>
                      <RoleTag user={u} />
                    </td>
                    <td className="d-none d-md-table-cell">{u.jobs}</td>
                    <td className="d-none d-lg-table-cell">{u.usage?.ai30 ?? 0}</td>
                    <td className="small text-muted d-none d-lg-table-cell">{timeAgo(u.createdAt)}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-light" onClick={() => onEdit(u)}>
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="card-body border-top d-flex justify-content-between align-items-center">
              <span className="small text-muted">
                {data.total} users · page {page} of {totalPages}
              </span>
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Prev
                </button>
                <button className="btn btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function SuperAdmin() {
  const { toast } = useApp();
  const { user: me, setUser } = useAuth();
  const [tab, setTab] = useState("logistics");
  const [logistics, setLogistics] = useState(null);
  const [services, setServices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    Promise.all([superadminApi.logistics(), superadminApi.services()])
      .then(([l, s]) => {
        setLogistics(l);
        setServices(s);
      })
      .catch((err) => toast(err.message, "danger"));
  }, [toast, version]);

  const changed = useCallback(() => setVersion((v) => v + 1), []);

  const saved = (user, message) => {
    toast(message);
    setEditing((prev) => (prev ? { ...prev, ...user } : prev));
    if (user.id === me.id) setUser(user);
    changed();
  };

  const deleted = (user) => {
    toast(`Permanently deleted ${user.name}`);
    setEditing(null);
    changed();
  };

  const tabs = [
    { id: "logistics", label: "Logistics", icon: "bi-graph-up" },
    { id: "services", label: "API & services", icon: "bi-hdd-network" },
    { id: "users", label: "Users", icon: "bi-people", count: logistics?.users?.pending },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Super Admin</h1>
          <p className="page-subtitle">User ops, app logistics, and service health — not job hunting.</p>
        </div>
      </div>

      <div className="tab-scroll mb-3">
        {tabs.map((t) => (
          <button key={t.id} className={`tab-btn ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            <i className={`bi ${t.icon}`} />
            {t.label}
            {t.count > 0 && <span className="side-badge">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "logistics" && <LogisticsTab data={logistics} />}
      {tab === "services" && <ServicesTab data={services} />}
      {tab === "users" && <UsersTab me={me.id} version={version} onEdit={setEditing} />}

      {editing && (
        <EditUserModal
          key={editing.id + (editing.updatedAt || "") + (editing.deletedAt || "")}
          user={editing}
          me={me.id}
          onClose={() => setEditing(null)}
          onSaved={saved}
          onDeleted={deleted}
        />
      )}
    </>
  );
}

export default SuperAdmin;
